import React, { useState } from 'react';
import { HiPhone, HiMail, HiLocationMarker, HiClock } from 'react-icons/hi';
import useScrollReveal from '../hooks/useScrollReveal';

const Contact = () => {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', project: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [headRef, headVisible] = useScrollReveal();
  const [cardsRef, cardsVisible] = useScrollReveal({ threshold: 0.1 });
  const [formRef, formVisible] = useScrollReveal({ threshold: 0.1 });

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 4000);
    setFormData({ name: '', email: '', phone: '', project: '', message: '' });
  };

  return (
    <section id="contact" className="py-24 lg:py-32 relative overflow-hidden bg-white border-t border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div ref={headRef} className={`max-w-3xl mb-20 transition-all duration-700 ${headVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="inline-block text-[11px] tracking-[0.3em] uppercase font-semibold text-accent-500 mb-6">
            Get In Touch
          </span>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-heading text-dark-800 leading-[1.1] mb-6">
            Let's Start Your{' '}
            <span className="italic text-primary-600">Investment</span>{' '}
            Journey
          </h2>
          <p className="text-dark-400 text-lg leading-relaxed font-light max-w-2xl">
            Connect with our expert consultants to explore prime plot opportunities and secure your future today.
          </p>
        </div>

        {/* Contact Cards Grid */}
        <div ref={cardsRef} className={`grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-16 transition-all duration-700 ${cardsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          {[
            { icon: <HiPhone className="w-6 h-6" />, label: 'Call Us', info: '85 5001 5003', link: 'tel:8550015003' },
            { icon: <HiMail className="w-6 h-6" />, label: 'Email Us', info: 'theprimeacres@gmail.com', link: 'mailto:theprimeacrs@gmail.com' },
            { icon: <HiLocationMarker className="w-6 h-6" />, label: 'Visit Us', info: 'CBD Belapur, Navi Mumbai', link: null },
            { icon: <HiClock className="w-6 h-6" />, label: 'Working Hours', info: 'Mon - Sat: 10 AM - 7 PM', link: null },
          ].map((item, i) => (
            <div key={i} 
              className="bg-white border border-gray-200 rounded-2xl p-6 hover:shadow-xl hover:border-primary-200 transition-all duration-500 group"
              style={{ transitionDelay: `${i * 100}ms` }}>
              <div className="w-14 h-14 bg-primary-50 rounded-2xl flex items-center justify-center mb-5 group-hover:bg-primary-600 transition-all duration-500">
                <span className="text-primary-600 group-hover:text-white transition-colors duration-500">{item.icon}</span>
              </div>
              <p className="text-[10px] text-dark-300 uppercase tracking-[0.25em] mb-2 font-medium">{item.label}</p>
              {item.link ? (
                <a href={item.link} className="text-dark-700 text-[15px] font-medium leading-snug hover:text-primary-600 transition-colors">
                  {item.info}
                </a>
              ) : (
                <p className="text-dark-700 text-[15px] font-medium leading-snug">{item.info}</p>
              )}
            </div>
          ))}
        </div>

        {/* Form Section */}
        <div ref={formRef} className={`max-w-4xl mx-auto transition-all duration-700 delay-200 ${formVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-8 md:p-12 lg:p-14 relative overflow-hidden border border-gray-200">
            {/* Decorative elements */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-primary-100/30 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
            
            <div className="relative z-10">
              <div className="text-center mb-10">
                <h3 className="text-2xl md:text-3xl font-heading text-dark-800 mb-3">Send Us a Message</h3>
                <p className="text-dark-400 text-sm font-light">Fill out the form below and we'll get back to you within 24 hours</p>
              </div>

              {submitted ? (
                <div className="text-center py-12">
                  <div className="w-20 h-20 bg-primary-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg shadow-primary-600/20">
                    <svg className="w-10 h-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <p className="font-heading text-2xl md:text-3xl text-dark-800 mb-3">Thank You for Reaching Out!</p>
                  <p className="text-dark-400 text-base font-light max-w-md mx-auto">Your message has been received. Our team will contact you shortly to discuss your investment plans.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-[11px] text-dark-400 uppercase tracking-[0.2em] mb-2 font-medium">Full Name *</label>
                      <input type="text" required value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full px-5 py-4 bg-white border border-gray-200 rounded-xl text-[15px] outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all" 
                        placeholder="John Doe" />
                    </div>
                    <div>
                      <label className="block text-[11px] text-dark-400 uppercase tracking-[0.2em] mb-2 font-medium">Email Address *</label>
                      <input type="email" required value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="w-full px-5 py-4 bg-white border border-gray-200 rounded-xl text-[15px] outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all" 
                        placeholder="john@example.com" />
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-[11px] text-dark-400 uppercase tracking-[0.2em] mb-2 font-medium">Phone Number *</label>
                      <input type="tel" required value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className="w-full px-5 py-4 bg-white border border-gray-200 rounded-xl text-[15px] outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all" 
                        placeholder="+91 98765 43210" />
                    </div>
                    <div>
                      <label className="block text-[11px] text-dark-400 uppercase tracking-[0.2em] mb-2 font-medium">Area of Interest</label>
                      <select value={formData.project}
                        onChange={(e) => setFormData({ ...formData, project: e.target.value })}
                        className="w-full px-5 py-4 bg-white border border-gray-200 rounded-xl text-[15px] text-dark-600 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all">
                        <option value="">Select a location</option>
                        <option>NAINA Plots</option>
                        <option>KSC New Town Plots</option>
                        <option>Near NMIA Airport</option>
                        <option>MTHL Corridor</option>
                        <option>Virar-Alibaug Corridor</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-[11px] text-dark-400 uppercase tracking-[0.2em] mb-2 font-medium">Your Message</label>
                    <textarea rows={5} value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      className="w-full px-5 py-4 bg-white border border-gray-200 rounded-xl text-[15px] outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 resize-none transition-all" 
                      placeholder="Tell us about your investment goals and requirements..." />
                  </div>
                  <button type="submit" className="w-full bg-primary-600 hover:bg-primary-700 text-white rounded-full py-5 text-[12px] font-semibold tracking-[0.15em] uppercase transition-all duration-500 flex items-center justify-center gap-3 shadow-lg hover:shadow-xl hover:shadow-primary-600/20">
                    Send Your Enquiry
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </button>
                  <p className="text-[11px] text-dark-300 text-center leading-relaxed font-light pt-2">
                    By submitting this form, you authorize Prime Acres representatives to contact you via Call, SMS, Email or WhatsApp regarding our services and offers.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>

        {/* Address Card */}
        <div className="mt-10 max-w-4xl mx-auto">
          <div className="bg-dark-800 rounded-2xl p-8 md:p-10 text-white">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div>
                <p className="text-[10px] text-white/50 uppercase tracking-[0.25em] mb-3 font-medium">Our Office</p>
                <p className="text-white/90 text-base leading-relaxed font-light">
                  Enjoy business hub,P floor,Tower number 07,<br />
                  Station building, Belapur CBD<br />
                  Navi Mumbai, Maharashtra.
                </p>
              </div>
              <div className="flex-shrink-0">
                <a href="https://maps.app.goo.gl/XWPrkPYa3TmsovLu7" target="_blank" rel="noopener noreferrer" 
                  className="inline-flex items-center gap-2 bg-white text-dark-800 rounded-full px-6 py-3.5 text-[11px] font-semibold tracking-wider uppercase hover:bg-gray-100 transition-all duration-300">
                  <HiLocationMarker className="w-4 h-4" />
                  Get Directions
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
